import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import moment, { Moment } from 'moment';

import DropdownMenu, { MenuItem } from '@ad-scripts/presentation/components/common/dropdown_menu_v2';
import { OpenerButton, ChevronIcon, ButtonTextWrapper } from '@ad-scripts/presentation/components/common/dropdown_menu';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import { setCurrentRevision } from 'src/presentation/components/pages/sidekick/threads/actions/revisionActions';
import sidekickRevisionsResource from 'src/actions/sidekick/sidekickRevisionsResource';
import useRouter from 'src/presentation/hooks/useRouter';
import { selectThreadMeta, setLastSavedRevision } from '../../actions/threadsUiSlice';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle as faCircleFull } from '@fortawesome/pro-solid-svg-icons/faCircle';
import sidekickAdminsResource from 'src/actions/sidekick/sidekickAdminsResource';
import { SidekickThreadFast } from 'src/models/Sidekick/SidekickThreads';
import { unescapeHTML } from 'src/actions/sidekick/utils';

const VersionBar = () => {
  const dispatch = useDispatch();
  const threadMeta = useSelector(selectThreadMeta);

  const { values: admins } = useResourceValues(sidekickAdminsResource);

  const { values: rawRevisions } = useResourceValues(sidekickRevisionsResource);
  const { history } = useRouter();

  const handleSelect = revisionId => {
    dispatch(setCurrentRevision({ revisionId, history }));
  };

  const adminsMap = useMemo(() => {
    if (!admins) return {};
    return admins.reduce((acc, admin) => ({ ...acc, [admin.id]: admin }), {});
  }, [admins]);

  const revisions = useMemo(
    () =>
      (rawRevisions || []).map(revision => ({
        ...revision,
        reason: unescapeHTML(revision.reason),
        value: revision.id,
        createdByString: adminsMap[revision.createdBy]?.name || adminsMap[revision.createdBy]?.email,
      })),
    [adminsMap, rawRevisions]
  );

  useEffect(() => {
    if (!rawRevisions || !rawRevisions.length) {
      return;
    }

    dispatch(setLastSavedRevision(rawRevisions[0].createdAt));
  }, [dispatch, rawRevisions]);

  const lastSaved = (threadMeta as SidekickThreadFast).updatedAt;
  return (
    <StyledContainer>
      <DropdownMenu
        options={revisions}
        onSelect={handleSelect}
        renderCustomOpener={({ setTargetRef, onToggle, isOpen }) => {
          const chevronIcon = <ChevronIcon data-isopen={isOpen} disabled={false} />;
          return (
            <OpenerButton aria-haspopup="true" onClick={onToggle} ref={setTargetRef}>
              <StyledContainer className="versionBar-menu">
                <StyledButtonTextWrapper>
                  <ThreadStatusIcon isEnabled={threadMeta.enabled} />
                  <NameContainer>{unescapeHTML(threadMeta.name)}</NameContainer>

                  {!!lastSaved && (
                    <StyledRevisionTextContainer>
                      <Tooltip message="Revisions" position="bottom" margin={18}>
                        <CreateTimeLabel>Last saved {getDateOrTimeStr(lastSaved)}</CreateTimeLabel>
                      </Tooltip>
                    </StyledRevisionTextContainer>
                  )}
                </StyledButtonTextWrapper>
                {!!lastSaved && chevronIcon}
              </StyledContainer>
            </OpenerButton>
          );
        }}
        customItemsRenderer={(items, onClose, onSelect) => customItems({ items, onClose, onSelect })}
      />
    </StyledContainer>
  );
};

const ThreadStatusIcon = ({ isEnabled }: { isEnabled: boolean }) => {
  return (
    <Tooltip message={isEnabled ? 'Active' : 'Inactive'} position="bottom" margin={18}>
      <StyledThreadStatusIconContainer isEnabled={isEnabled}>
        <StyledFontAwesomeIcon icon={faCircleFull as any} />
      </StyledThreadStatusIconContainer>
    </Tooltip>
  );
};

const customItems = ({ items, onClose, onSelect }) => {
  if (!items) {
    return null;
  }

  return (
    <>
      {items.map((item, index) => {
        return (
          <MenuItem
            option={item}
            isFocused={false}
            className="menu-item"
            data-aid={`menu-item-${index}`}
            isMultiSelect={false}
            onClose={onClose}
            onSelect={onSelect}
            key={index}
            renderCustomContent={option => {
              return (
                <MenuItemContainer>
                  <AuthorTimeColumn>
                    <span>{getDateOrTimeStr(option.createdAt)}</span>
                    <StyledTooltip message={option.createdByString}>{option.createdByString}</StyledTooltip>
                  </AuthorTimeColumn>
                  <RevisionNameColumn>{option.reason}</RevisionNameColumn>
                </MenuItemContainer>
              );
            }}
          />
        );
      })}
    </>
  );
};

const getDateOrTimeStr = (date: Date) => {
  const versionDate: Moment | null = moment.utc(date).local();
  const isToday = moment(versionDate).format('DD/MM/YYYY') === moment().format('DD/MM/YYYY');
  return isToday ? versionDate.format('h:mmA') : versionDate.format('MMM D');
};

const CreateTimeLabel = styled.span`
  color: ${p => p.theme.colors.gray700};
  font-weight: 400;
`;

const StyledRevisionTextContainer = styled.div`
  padding-left: 16px;
`;

const NameContainer = styled.span`
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
`;

const StyledThreadStatusIconContainer = styled.div`
  color: ${p => (p.isEnabled ? p.theme.colors.green500 : p.theme.colors.gray700)};
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)`
  
  font-size: 8px;
  margin-right: 8px;
  position: relative;
  top: -1px;
}

`;

const MenuItemContainer = styled.div`
  padding: 4px 0;
  display: flex;
  justify-content: start;
  width: 370px;
  gap: 12px;
`;

const AuthorTimeColumn = styled.div`
  color: ${p => p.theme.colors.gray700};
  display: flex;
  flex-direction: column;
  max-width: 120px;
  width: 120px;
`;

const StyledTooltip = styled(Tooltip)`
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  text-wrap: nowrap;
`;

const RevisionNameColumn = styled.div`
  max-width: 170px;
  width: 170px;
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledButtonTextWrapper = styled(ButtonTextWrapper)`
  display: flex;
  width: 100%;
`;

export default VersionBar;
